import { useBuyListingMutation, MarketListing } from '@http';
import { IconMinus, IconPlus } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAppStore } from '@stores';
import { useState } from 'react';

interface BuyListingProps {
  listing: MarketListing;
  onClose?: () => void;
}

export function BuyListing({ listing, onClose }: BuyListingProps) {
  const [amount, setAmount] = useState(listing.amount);

  const { t } = useTranslation(undefined, { keyPrefix: 'PlayerMarket.Buy' });
  const { t: tItems } = useTranslation(undefined, { keyPrefix: 'Items' });
  const { mutateAsync: buyAsync } = useBuyListingMutation();
  const { userData, isFetching, refetch, setIsFetching } = useAppStore();
  const queryClient = useQueryClient();

  const sub = () => setAmount(Math.max(amount - 1, 1));
  const add = () => setAmount(Math.min(amount + 1, listing.amount));

  const total = Number.isFinite(amount * listing.price) ? amount * listing.price : 0;
  const canBuy = amount > 0 && amount <= listing.amount && total <= userData.credits;
  const canAfford = total <= userData.credits;
  const ownCredits = userData.credits;

  const buy = async () => {
    if (isFetching) return;
    setIsFetching(true);

    await buyAsync({ id: listing.id, amount });

    queryClient.invalidateQueries({ queryKey: ['tradingListings'] });
    onClose?.();
    refetch();

    toast(t('success'), { type: 'success' });
  };

  return (
    <div className="p-2">
      <p>{tItems(listing.key)}</p>

      <div className="flex w-full items-center justify-between my-4">
        <button className="mod-decrease-level" onClick={sub}>
          <IconMinus />
        </button>

        <div className="relative mx-4 w-full">
          <input
            onChange={e => setAmount(parseInt(e.target.value))}
            className="input border border-secondary"
            value={amount}
            type="number"
          />

          <p className={`${amount > listing.amount && 'text-red-500'} absolute bottom-0.25 right-1 text-xs text-gray-400`}>
            {listing.amount}
          </p>
        </div>

        <button className="mod-increase-level" onClick={add}>
          <IconPlus />
        </button>
      </div>

      <p className="text-lime-500 text-sm mt-2">
        {t('total', { total })}
      </p>

      <p className={`${canAfford ? 'text-lime-500' : 'text-red-500'} text-sm mt-1`}>
        {t('own', { credits: ownCredits })}
      </p>

      <Button disabled={!canBuy} className="mt-4" onClick={buy}>
        {t('buy')}
      </Button>
    </div>
  );
}